
import { useState } from 'react';
import { format } from 'date-fns';
import { AlertCircle, Check, Download, FileDown, Filter, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useToast } from '@/hooks/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ContextualHelpSystem } from '../../certificates/ContextualHelpSystem';

interface ExportTemplate {
  id: string;
  name: string;
  format: string;
  filters: Record<string, any>;
  regulatorySettings?: Record<string, any>;
}

interface EnhancedExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultFormat?: 'csv' | 'xlsx' | 'pdf' | 'regulatory';
  defaultState?: string;
  onExport?: (exportConfig: Record<string, any>) => void;
}

export function EnhancedExportDialog({
  open,
  onOpenChange,
  defaultFormat = 'csv',
  defaultState,
  onExport
}: EnhancedExportDialogProps) {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<string>('export');
  const [exportFormat, setExportFormat] = useState<string>(defaultFormat);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [saveAsTemplate, setSaveAsTemplate] = useState<boolean>(false);
  const [templateName, setTemplateName] = useState<string>('');
  const [previewCount, setPreviewCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  
  // Filters state
  const [filters, setFilters] = useState({
    state: defaultState || '',
    documentType: '',
    dateRange: {
      startDate: format(new Date(new Date().setMonth(new Date().getMonth() - 1)), 'yyyy-MM-dd'),
      endDate: format(new Date(), 'yyyy-MM-dd'),
    },
    status: '',
  });
  
  // Regulatory export settings
  const [regulatorySettings, setRegulatorySettings] = useState({
    includeHeader: true,
    delimiter: 'comma',
    quoteStrings: true,
    dateFormat: 'MM/DD/YYYY',
    submissionFormat: 'standard',
  });
  
  // Mock templates (would be fetched from the database in a real implementation)
  const mockTemplates: ExportTemplate[] = [
    {
      id: '1',
      name: 'Florida Quarterly Report',
      format: 'regulatory',
      filters: { state: 'FL', documentType: 'certificate' },
      regulatorySettings: { submissionFormat: 'florida' }
    },
    {
      id: '2',
      name: 'All Certificates (CSV)',
      format: 'csv',
      filters: { documentType: 'certificate' }
    },
    {
      id: '3',
      name: 'Recent Documents',
      format: 'xlsx',
      filters: { 
        dateRange: {
          startDate: format(new Date(new Date().setDate(new Date().getDate() - 30)), 'yyyy-MM-dd'),
          endDate: format(new Date(), 'yyyy-MM-dd')
        }
      }
    }
  ];
  
  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    
    // In a real implementation, this would update the preview count from the server
    // For now, we'll just set a random number
    setPreviewCount(Math.floor(Math.random() * 500) + 50);
  };
  
  const handleDateRangeChange = (key: 'startDate' | 'endDate', value: string) => {
    setFilters(prev => ({
      ...prev,
      dateRange: {
        ...prev.dateRange,
        [key]: value
      }
    }));
    
    // Update preview count as above
    setPreviewCount(Math.floor(Math.random() * 500) + 50);
  };
  
  const handleRegulatorySettingChange = (key: string, value: any) => {
    setRegulatorySettings(prev => ({
      ...prev,
      [key]: value
    }));
  };
  
  const handleExport = () => {
    setIsLoading(true);
    
    // Create the export configuration
    const exportConfig = {
      format: exportFormat,
      filters,
      regulatorySettings: exportFormat === 'regulatory' ? regulatorySettings : undefined,
      timestamp: new Date().toISOString(),
    };
    
    // If saving as template
    if (saveAsTemplate && templateName) {
      setIsSaving(true);
      // Save template logic would go here
      // For now, just simulate a delay
      setTimeout(() => {
        setIsSaving(false);
        toast({
          title: "Template Saved",
          description: `Export template "${templateName}" has been saved.`,
        });
      }, 1000);
    }
    
    // Simulate export process
    setTimeout(() => {
      setIsLoading(false);
      
      if (onExport) {
        onExport(exportConfig);
      }
      
      toast({
        title: "Export Complete",
        description: `Successfully exported ${previewCount} documents in ${exportFormat.toUpperCase()} format.`,
      });
      
      onOpenChange(false);
    }, 2000);
  };
  
  const handleTemplateSelect = (templateId: string) => {
    const template = mockTemplates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(templateId);
      setExportFormat(template.format);
      setFilters({ ...filters, ...template.filters });
      if (template.regulatorySettings) {
        setRegulatorySettings({ ...regulatorySettings, ...template.regulatorySettings });
      }
      
      // Update preview count
      setPreviewCount(Math.floor(Math.random() * 500) + 50);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[80vh] flex flex-col">
        <DialogHeader className="flex flex-row items-center justify-between">
          <div>
            <DialogTitle className="flex items-center gap-2">
              <FileDown className="h-5 w-5" />
              Advanced Document Export
            </DialogTitle>
            <DialogDescription>
              Configure and export documents in various formats for reporting, analysis, or regulatory compliance.
            </DialogDescription>
          </div>
          <ContextualHelpSystem pageId="export" />
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
          <TabsList className="mb-4">
            <TabsTrigger value="export">Export Settings</TabsTrigger>
            <TabsTrigger value="templates">Saved Templates</TabsTrigger>
            {exportFormat === 'regulatory' && (
              <TabsTrigger value="regulatory">Regulatory Format</TabsTrigger>
            )}
          </TabsList>
          
          <div className="flex-1 overflow-auto pr-2">
            <TabsContent value="export" className="space-y-6 mt-0">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Export Format</Label>
                  <Select
                    value={exportFormat}
                    onValueChange={setExportFormat}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select format" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="csv">CSV (Comma Separated)</SelectItem>
                      <SelectItem value="xlsx">Excel Spreadsheet (XLSX)</SelectItem>
                      <SelectItem value="pdf">PDF Document</SelectItem>
                      <SelectItem value="regulatory">Regulatory Format</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  {exportFormat === 'regulatory' && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Regulatory format follows state-specific requirements for official reporting.
                    </p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label>State</Label>
                  <Select
                    value={filters.state || "none"}
                    onValueChange={(value) => handleFilterChange('state', value === "none" ? "" : value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All States" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">All States</SelectItem>
                      <SelectItem value="FL">Florida</SelectItem>
                      <SelectItem value="TX">Texas</SelectItem>
                      <SelectItem value="CA">California</SelectItem>
                      <SelectItem value="NY">New York</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Document Type</Label>
                  <Select
                    value={filters.documentType || "none"}
                    onValueChange={(value) => handleFilterChange('documentType', value === "none" ? "" : value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All Document Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">All Document Types</SelectItem>
                      <SelectItem value="certificate">Certificates</SelectItem>
                      <SelectItem value="report">Reports</SelectItem>
                      <SelectItem value="regulatory">Regulatory Documents</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={filters.status || "none"}
                    onValueChange={(value) => handleFilterChange('status', value === "none" ? "" : value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">All Statuses</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Date Range</Label>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="text-xs">Start Date</Label>
                    <Input
                      type="date"
                      value={filters.dateRange.startDate}
                      onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">End Date</Label>
                    <Input
                      type="date"
                      value={filters.dateRange.endDate}
                      onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="saveTemplate"
                  checked={saveAsTemplate}
                  onCheckedChange={(checked) => setSaveAsTemplate(checked === true)}
                />
                <Label htmlFor="saveTemplate">Save as export template</Label>
              </div>
              
              {saveAsTemplate && (
                <div className="space-y-2">
                  <Label htmlFor="templateName">Template Name</Label>
                  <Input
                    id="templateName"
                    placeholder="E.g., Monthly Certificates Export"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                  />
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="templates" className="space-y-6 mt-0">
              {mockTemplates.length === 0 ? (
                <div className="text-center py-10 border rounded-lg">
                  <p className="text-muted-foreground">No saved templates found</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Save your export configurations as templates for quick access.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {mockTemplates.map((template) => (
                    <div
                      key={template.id}
                      className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                        selectedTemplate === template.id ? 'border-primary bg-primary/5' : ''
                      }`}
                      onClick={() => handleTemplateSelect(template.id)}
                    >
                      <div className="flex justify-between">
                        <div>
                          <h4 className="font-medium flex items-center">
                            {template.name}
                            {selectedTemplate === template.id && (
                              <Check className="ml-2 h-4 w-4 text-primary" />
                            )}
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            Format: {template.format.toUpperCase()}
                            {template.filters.state && ` • State: ${template.filters.state}`}
                            {template.filters.documentType && ` • Type: ${template.filters.documentType}`}
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTemplateSelect(template.id);
                            setActiveTab('export');
                          }}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
            
            {exportFormat === 'regulatory' && (
              <TabsContent value="regulatory" className="space-y-6 mt-0">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Submission Format</Label>
                    <Select
                      value={regulatorySettings.submissionFormat}
                      onValueChange={(value) => handleRegulatorySettingChange('submissionFormat', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="standard">Standard Format</SelectItem>
                        <SelectItem value="florida">Florida DMV Format</SelectItem>
                        <SelectItem value="texas">Texas DPS Format</SelectItem>
                        <SelectItem value="california">California DMV Format</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">
                      Select the specific format required by the regulatory authority
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Date Format</Label>
                    <RadioGroup
                      value={regulatorySettings.dateFormat}
                      onValueChange={(value) => handleRegulatorySettingChange('dateFormat', value)}
                      className="flex flex-col space-y-1"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="MM/DD/YYYY" id="date-format-1" />
                        <Label htmlFor="date-format-1">MM/DD/YYYY</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="DD/MM/YYYY" id="date-format-2" />
                        <Label htmlFor="date-format-2">DD/MM/YYYY</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="YYYY-MM-DD" id="date-format-3" />
                        <Label htmlFor="date-format-3">YYYY-MM-DD</Label>
                      </div>
                    </RadioGroup>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Delimiter</Label>
                    <RadioGroup
                      value={regulatorySettings.delimiter}
                      onValueChange={(value) => handleRegulatorySettingChange('delimiter', value)}
                      className="flex flex-col space-y-1"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="comma" id="delimiter-1" />
                        <Label htmlFor="delimiter-1">Comma (,)</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="tab" id="delimiter-2" />
                        <Label htmlFor="delimiter-2">Tab</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="pipe" id="delimiter-3" />
                        <Label htmlFor="delimiter-3">Pipe (|)</Label>
                      </div>
                    </RadioGroup>
                  </div>
                  
                  <div className="flex flex-col space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="includeHeader"
                        checked={regulatorySettings.includeHeader}
                        onCheckedChange={(checked) => 
                          handleRegulatorySettingChange('includeHeader', checked === true)
                        }
                      />
                      <Label htmlFor="includeHeader">Include header row</Label>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="quoteStrings"
                        checked={regulatorySettings.quoteStrings}
                        onCheckedChange={(checked) => 
                          handleRegulatorySettingChange('quoteStrings', checked === true)
                        }
                      />
                      <Label htmlFor="quoteStrings">Quote string values</Label>
                    </div>
                  </div>
                </div>
              </TabsContent>
            )}
          </div>
        </Tabs>
        
        <div className="bg-muted/40 p-3 rounded-md flex justify-between items-center mt-4">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <span>
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin inline mr-1" />
              ) : (
                <strong>{previewCount}</strong>
              )} 
              documents will be exported
            </span>
          </div>
          
          {previewCount > 1000 && (
            <p className="text-xs text-amber-600">
              <AlertCircle className="h-3 w-3 inline mr-1" />
              Large export may take some time
            </p>
          )}
        </div>
        
        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          {saveAsTemplate && (
            <Button 
              variant="outline" 
              onClick={() => {
                if (templateName) {
                  // Save template logic here
                  toast({
                    title: "Template Saved",
                    description: `Export template "${templateName}" has been saved.`,
                  });
                  setSaveAsTemplate(false);
                  setTemplateName('');
                } else {
                  toast({
                    variant: "destructive",
                    title: "Template Name Required",
                    description: "Please provide a name for your template",
                  });
                }
              }}
              disabled={isLoading || !templateName}
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Template Only"
              )}
            </Button>
          )}
          <Button 
            onClick={handleExport} 
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Export {previewCount} Documents
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
